import React, { useMemo } from 'react';
import { t, defineMessages } from '@sm/intl';
import { Box } from '@wds/box';
import { Grid } from '@wds/grid';
import useStyles from './useStyles';
import { getSrc } from '~app/helpers/assetLoading';
import EPImage from '~app/components/endpages/components/Image';
import { google, ibm, chime, ebay, mailchimp, cvsHealth, samsung, adidas, toyota } from '~static/images/icons';

export type ColorProp = 'sabaeus' | 'canvas';

type Props = {
  darkBackground?: boolean;
  utSource2?: string;
};

const copyDescription = '[Type: label][Vis: low] - The aria label for a corporate logo';
export const SPLIT_HERO_COPY = defineMessages({
  GOOGLE_LABEL: {
    id: 'LogoWall.GoogleLogo',
    defaultMessage: 'Google logo',
    description: copyDescription,
  },
  IBM_LABEL: {
    id: 'LogoWall.IBMLogo',
    defaultMessage: 'IBM logo',
    description: copyDescription,
  },
  EBAY_LABEL: {
    id: 'LogoWall.EbayLogo',
    defaultMessage: 'Ebay logo',
    description: copyDescription,
  },
  MAILCHIMP_LABEL: {
    id: 'LogoWall.MailchimpLogo',
    defaultMessage: 'Mailchimp Logo',
    description: copyDescription,
  },
  CVS_LABEL: {
    id: 'LogoWall.CVSLogo',
    defaultMessage: 'CVS logo',
    description: copyDescription,
  },
  CHIME_LABEL: {
    id: 'LogoWall.ChimeLogo',
    defaultMessage: 'Chime logo',
    description: copyDescription,
  },
  SAMSUNG_LABEL: {
    id: 'LogoWall.SamsungLogo',
    defaultMessage: 'Samsung logo',
    description: copyDescription,
  },
  ADIDAS_LABEL: {
    id: 'LogoWall.AdidasLogo',
    defaultMessage: 'Adidas logo',
    description: copyDescription,
  },
  TOYOTA_LABEL: {
    id: 'LogoWall.ToyotaLogo',
    defaultMessage: 'Toyota logo',
    description: copyDescription,
  },
});

const ALL_LOGOS = [
  { key: 'google', img: google, label: SPLIT_HERO_COPY.GOOGLE_LABEL },
  { key: 'toyota', img: toyota, label: SPLIT_HERO_COPY.TOYOTA_LABEL },
  { key: 'ibm', img: ibm, label: SPLIT_HERO_COPY.IBM_LABEL },
  { key: 'adidas', img: adidas, label: SPLIT_HERO_COPY.ADIDAS_LABEL },
  { key: 'ebay', img: ebay, label: SPLIT_HERO_COPY.EBAY_LABEL },
  { key: 'samsung', img: samsung, label: SPLIT_HERO_COPY.SAMSUNG_LABEL },
  { key: 'mailchimp', img: mailchimp, label: SPLIT_HERO_COPY.MAILCHIMP_LABEL },
  { key: 'cvsHealth', img: cvsHealth, label: SPLIT_HERO_COPY.CVS_LABEL },
  { key: 'chime', img: chime, label: SPLIT_HERO_COPY.CHIME_LABEL },
];

const LOGO_ORDERS: Record<string, string[]> = {
  rebrandEpFormIntl: ['google', 'toyota', 'ibm', 'adidas', 'ebay', 'samsung'],
  default: ['google', 'ibm', 'ebay', 'mailchimp', 'cvsHealth', 'chime'],
};

const LogoWall = ({ darkBackground = false, utSource2 = '' }: Props): React.ReactElement => {
  const { logo } = useStyles({ darkBackground });

  const logos = useMemo(() => {
    const order = LOGO_ORDERS[utSource2] || LOGO_ORDERS.default;
    return order
      .map(key => ALL_LOGOS.find(l => l.key === key))
      .filter((l): l is (typeof ALL_LOGOS)[number] => Boolean(l))
      .map(l => ({ src: getSrc(l.img), alt: t(l.label) }));
  }, [utSource2]);

  return (
    <Box>
      <Grid noBleeds noGutters>
        {logos.map(({ src, alt }) => (
          <Grid.Item key={alt} xs={6} sm={4} md={4} lg={2} xl={2}>
            <EPImage
              src={src}
              alt={alt}
              width={140}
              height={68}
              className={logo}
              sizes="(max-width: 576px) 50vw, (max-width: 768px) 33vw, (max-width: 992px) 25vw, 16vw"
            />
          </Grid.Item>
        ))}
      </Grid>
    </Box>
  );
};

export default LogoWall;
